import { ReactNode } from 'react';
import { Tooltip } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import RepeatIcon from '@mui/icons-material/Repeat';
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  gridFilterModelSelector,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { StyledQuickFilter } from '@components/DataGrid/StyledComponents';
import DensityMenuButton from '@components/DataGrid/ToolbarItems/DensityMenu';
import { DeductionType } from '@api/Trinium/Endpoints/Settlements/types';

/** Stable id so the toggles' filter can be found/removed without touching manual filters.
 *  Both toggles share the id, which makes them mutually exclusive. */
const TYPE_FILTER_ID = 'deduction-type';

function DeductionTypeToggle({ type, title, children }: { type: DeductionType; title: string; children: ReactNode }) {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const isActive = filterModel.items.some((item) => item.id === TYPE_FILTER_ID && item.value === type);

  const toggle = () => {
    const items = filterModel.items.filter((item) => item.id !== TYPE_FILTER_ID);
    apiRef.current.setFilterModel({
      ...filterModel,
      items: isActive ? items : [...items, { id: TYPE_FILTER_ID, field: 'deductionType', operator: 'equals', value: type }]
    });
  };

  return (
    <Tooltip title={title}>
      <ToolbarButton
        aria-pressed={isActive}
        color={isActive ? 'primary' : 'default'}
        onClick={toggle}>
        {children}
      </ToolbarButton>
    </Tooltip>
  );
}

export function DeductionsToolbar() {
  return (
    <Toolbar style={{ justifyContent: 'flex-end' }}>
      <Tooltip title='Columns'>
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize='small' />
        </ColumnsPanelTrigger>
      </Tooltip>
      <Tooltip title='Filters'>
        <FilterPanelTrigger render={<ToolbarButton />}>
          <FilterListIcon fontSize='small' />
        </FilterPanelTrigger>
      </Tooltip>
      <DeductionTypeToggle
        type='One-Time'
        title='Show only one-time deductions'>
        <LooksOneIcon fontSize='small' />
      </DeductionTypeToggle>
      <DeductionTypeToggle
        type='Recurring'
        title='Show only recurring deductions'>
        <RepeatIcon fontSize='small' />
      </DeductionTypeToggle>
      <DensityMenuButton />
      <StyledQuickFilter />
    </Toolbar>
  );
}
